import { AdminUserRecord } from './adminManagement';
import { AdminRoleRecord } from 'src/utils/rbacCatalog';

/** Map API admin payload fields to the shape the UI expects. */
export function normalizeAdminUser(raw: Record<string, unknown>): AdminUserRecord {
  const role = (raw.role ?? raw.admin_role) as AdminUserRecord['role'] | undefined;
  const user = (raw.user ?? raw.account ?? raw.platform_user) as Record<string, unknown> | undefined;

  const accountId =
    (raw.account_id as string) ??
    (raw.accountId as string) ??
    (raw.user_id as string) ??
    (raw.userId as string) ??
    (user?.id as string) ??
    (user?._id as string) ??
    '';

  const roleId =
    (raw.role_id as string) ??
    (raw.roleId as string) ??
    role?.id ??
    role?._id ??
    '';

  const joinedName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
  const userName =
    (user?.name as string) ??
    (user?.full_name as string) ??
    (user?.fullName as string) ??
    (joinedName || undefined);

  const userEmail = (user?.email as string) ?? (user?.mail as string) ?? undefined;

  return {
    ...(raw as AdminUserRecord),
    _id: (raw._id as string) ?? (raw.id as string),
    account_id: accountId,
    role_id: roleId,
    status: (raw.status as string) ?? 'active',
    is_super_admin: Boolean(raw.is_super_admin ?? raw.isSuperAdmin),
    role,
    name: (raw.name as string) ?? userName,
    email: (raw.email as string) ?? userEmail,
    user: user
      ? {
          id: (user.id as string) ?? (user._id as string),
          email: userEmail,
          name: userName,
        }
      : undefined,
  };
}

/** Value for the role select that matches the admin's current role. */
export function resolveRoleSelectValue(
  admin: AdminUserRecord | null | undefined,
  roles: AdminRoleRecord[],
): string {
  if (!admin) return '';

  const roleId = admin.role_id;
  const roleKey = admin.role?.key;

  if (roleId) {
    const byId = roles.find((r) => {
      const id = r._id ?? r.key;
      return id === roleId || String(id) === String(roleId);
    });
    if (byId) return byId._id ?? byId.key;
  }

  if (roleKey) {
    const byKey = roles.find((r) => r.key === roleKey);
    if (byKey) return byKey._id ?? byKey.key;
  }

  if (admin.role?.name) {
    const byName = roles.find((r) => r.name === admin.role?.name);
    if (byName) return byName._id ?? byName.key;
  }

  return roleId ?? '';
}

export function getAdminAccountIdDisplay(admin: AdminUserRecord): string {
  return admin.account_id || admin.user?.id || '—';
}

export function getAdminNameDisplay(admin: AdminUserRecord): string {
  return admin.name ?? admin.user?.name ?? '—';
}

export function getAdminEmailDisplay(admin: AdminUserRecord): string {
  return admin.email ?? admin.user?.email ?? '—';
}
